import React from "react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import NewsletterSubscribeMain from './NewsletterSubscribeMain';

const blogPosts = [
  {
    id: 1,
    title: "Zero Trust Architecture for AWS: A Practical Guide",
    excerpt: "Implementing zero trust principles in AWS using IAM, Service Control Policies, and network segmentation techniques.",
    date: "July 28, 2025",
    readTime: "8 min read",
    tags: ["AWS", "Zero Trust", "IAM"],
    image: "/blog-zero-trust.jpg"
  },
  {
    id: 2,
    title: "Automating SOC2 Compliance with Terraform",
    excerpt: "How we helped a fintech startup achieve SOC2 compliance in 6 weeks using infrastructure-as-code security controls.",
    date: "June 15, 2025",
    readTime: "10 min read",
    tags: ["Compliance", "Terraform", "SOC2"],
    image: "/blog-soc2.jpg"
  },
  {
    id: 3,
    title: "Kubernetes Hardening: Beyond the Basics",
    excerpt: "Advanced security measures for production Kubernetes clusters based on NSA/CISA guidelines.",
    date: "May 3, 2025",
    readTime: "12 min read",
    tags: ["Kubernetes", "NSA", "Hardening"],
    image: "/blog-k8s.jpg"
  },
  {
    id: 4,
    title: "Incident Response Automation with AWS Lambda",
    excerpt: "Building serverless detection and response pipelines that trigger on GuardDuty findings.",
    date: "April 18, 2025",
    readTime: "6 min read",
    tags: ["AWS", "Lambda", "Security Automation"],
    image: "/blog-lambda.jpg"
  },
  {
    id: 5,
    title: "Azure Sentinel vs AWS GuardDuty: Enterprise Threat Detection Compared",
    excerpt: "Detailed comparison of cloud-native threat detection capabilities between Azure and AWS security services.",
    date: "March 22, 2025",
    readTime: "9 min read",
    tags: ["Azure", "AWS", "SIEM"],
    image: "/blog-sentinel-vs-guardduty.jpg"
  },
  {
    id: 6,
    title: "GCP Organization Policies: Enforcing Security at Scale",
    excerpt: "How to implement and automate security guardrails across Google Cloud organizations and folders.",
    date: "February 14, 2025",
    readTime: "7 min read",
    tags: ["GCP", "Security", "Automation"],
    image: "/blog-gcp-policies.jpg"
  },
  {
    id: 7,
    title: "Terraform Secure Modules: Building Reusable Security Patterns",
    excerpt: "Design principles for creating Terraform modules that enforce security best practices by default.",
    date: "January 30, 2025",
    readTime: "11 min read",
    tags: ["Terraform", "IaC", "DevSecOps"],
    image: "/blog-terraform-modules.jpg"
  },
  {
    id: 8,
    title: "Multi-Cloud Identity Bridge: Azure AD + AWS IAM + GCP IAM",
    excerpt: "Implementing centralized identity management across AWS, Azure, and Google Cloud platforms.",
    date: "December 5, 2024",
    readTime: "14 min read",
    tags: ["Multi-Cloud", "IAM", "Azure AD"],
    image: "/blog-multicloud-iam.jpg"
  },
  {
    id: 9,
    title: "Serverless Security: Protecting AWS Lambda, Azure Functions, and Cloud Run",
    excerpt: "Security patterns for serverless architectures across major cloud providers.",
    date: "November 18, 2024",
    readTime: "8 min read",
    tags: ["Serverless", "AWS Lambda", "Azure Functions"],
    image: "/blog-serverless-security.jpg"
  },
  {
    id: 10,
    title: "Azure Policy vs AWS SCP: Enterprise Governance Compared",
    excerpt: "Deep dive into policy enforcement capabilities for Azure and AWS multi-account environments.",
    date: "October 29, 2024",
    readTime: "10 min read",
    tags: ["Azure", "AWS", "Governance"],
    image: "/blog-policy-compare.jpg"
  },
  {
    id: 11,
    title: "GCP BeyondCorp: Implementing Zero Trust for Google Cloud",
    excerpt: "Practical guide to deploying Google's BeyondCorp zero trust model in enterprise environments.",
    date: "September 14, 2024",
    readTime: "12 min read",
    tags: ["GCP", "Zero Trust", "BeyondCorp"],
    image: "/blog-beyondcorp.jpg"
  },
  {
    id: 12,
    title: "Terraform Security: Preventing Drift and Configuration Attacks",
    excerpt: "Advanced techniques to secure your Terraform pipelines against drift and malicious changes.",
    date: "August 22, 2024",
    readTime: "9 min read",
    tags: ["Terraform", "DevSecOps", "Supply Chain"],
    image: "/blog-terraform-security.jpg"
  },
  {
    id: 13,
    title: "Azure Arc: Securing Hybrid Cloud Environments",
    excerpt: "Security considerations for managing on-prem and multi-cloud resources through Azure Arc.",
    date: "July 8, 2024",
    readTime: "7 min read",
    tags: ["Azure", "Hybrid Cloud", "Compliance"],
    image: "/blog-azure-arc.jpg"
  },
  {
    id: 14,
    title: "AWS IAM Access Analyzer vs GCP Policy Troubleshooter",
    excerpt: "Comparing cloud provider tools for identifying and resolving over-permissioned IAM policies.",
    date: "June 1, 2024",
    readTime: "6 min read",
    tags: ["AWS", "GCP", "IAM"],
    image: "/blog-iam-tools.jpg"
  },
  {
    id: 15,
    title: "Container Security Scanning: CI/CD Pipeline Integration",
    excerpt: "Implementing automated container vulnerability scanning in CI/CD pipelines with actionable results.",
    date: "May 15, 2024",
    readTime: "7 min read",
    tags: ["Containers", "CI/CD", "Security Scanning"],
    image: "/blog-container-scanning.jpg"
  },
  {
    id: 16,
    title: "Secrets Management: Beyond Environment Variables",
    excerpt: "Modern approaches to secrets management in cloud-native applications with rotation automation.",
    date: "April 22, 2024",
    readTime: "9 min read",
    tags: ["Secrets", "Security", "DevOps"],
    image: "/blog-secrets-management.jpg"
  },
  {
    id: 17,
    title: "Cloud Security Posture Management (CSPM) Implementation Guide",
    excerpt: "Building continuous compliance monitoring across AWS, Azure, and GCP with automated remediation.",
    date: "March 30, 2024",
    readTime: "11 min read",
    tags: ["CSPM", "Compliance", "Cloud Security"],
    image: "/blog-cspm.jpg"
  },
  {
    id: 18,
    title: "OWASP Top 10 for APIs: Practical Mitigation Strategies",
    excerpt: "Implementing security controls for the OWASP API Security Top 10 vulnerabilities in production environments.",
    date: "February 18, 2024",
    readTime: "8 min read",
    tags: ["API Security", "OWASP", "Web Security"],
    image: "/blog-owasp-api.jpg"
  },
  {
    id: 19,
    title: "Security Chaos Engineering: Breaking Your Systems on Purpose",
    excerpt: "Implementing controlled failure injection to build resilient security systems and response processes.",
    date: "January 12, 2024",
    readTime: "10 min read",
    tags: ["Chaos Engineering", "Resilience", "Testing"],
    image: "/blog-chaos-engineering.jpg"
  },
  {
    id: 20,
    title: "Cloud Data Loss Prevention (DLP) Implementation Guide",
    excerpt: "Protecting sensitive data across cloud storage, databases, and applications with automated classification.",
    date: "December 5, 2023",
    readTime: "12 min read",
    tags: ["DLP", "Data Protection", "Compliance"],
    image: "/blog-dlp.jpg"
  },
  {
    id: 21,
    title: "Zero Day Response: Building an Effective Vulnerability Management Program",
    excerpt: "Creating processes and tools for rapid response to critical vulnerabilities and zero-day threats.",
    date: "November 20, 2023",
    readTime: "9 min read",
    tags: ["Vulnerability", "Zero Day", "Incident Response"],
    image: "/blog-zero-day.jpg"
  },
  {
    id: 22,
    title: "Security Orchestration, Automation, and Response (SOAR) Implementation",
    excerpt: "Building automated security workflows for incident response, threat hunting, and security operations.",
    date: "October 15, 2023",
    readTime: "11 min read",
    tags: ["SOAR", "Automation", "Incident Response"],
    image: "/blog-soar.jpg"
  },
  {
    id: 23,
    title: "Cloud Network Security: Beyond Traditional Firewalls",
    excerpt: "Modern approaches to cloud network security using microsegmentation, service meshes, and zero trust principles.",
    date: "September 8, 2023",
    readTime: "10 min read",
    tags: ["Network Security", "Cloud", "Zero Trust"],
    image: "/blog-cloud-network.jpg"
  },
  {
    id: 24,
    title: "DevSecOps Metrics: Measuring Security Program Effectiveness",
    excerpt: "Key performance indicators and metrics for measuring and improving DevSecOps program effectiveness.",
    date: "August 22, 2023",
    readTime: "8 min read",
    tags: ["DevSecOps", "Metrics", "KPIs"],
    image: "/blog-devsecops-metrics.jpg"
  }
];

const BlogPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 text-gray-900 font-sans antialiased">
      {/* Header with Animated Gradient */}
      <header className="bg-gradient-to-r from-blue-900 to-blue-700 text-white text-center py-12 shadow-xl relative overflow-hidden">
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <img
            src="/HunterFortress_Logo_Shield_Crosshairs_Optimized.png"
            alt="HunterFortress Logo"
            className="mx-auto h-24 mb-6 drop-shadow-lg"
          />
          <motion.h1 
            className="text-5xl font-extrabold tracking-tight mb-3"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, type: "spring" }}
          >
            Security Blog
          </motion.h1>
          <motion.p 
            className="text-xl font-light max-w-2xl mx-auto opacity-90"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Tactical insights from the frontlines of cloud security
          </motion.p>
        </motion.div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <motion.section 
          className="mb-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">
            <span className="border-b-4 border-blue-500 pb-2">Latest Articles</span>
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.article
                key={post.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="h-48 bg-blue-100 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-gray-500">{post.date}</span>
                    <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">{post.readTime}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{post.title}</h3>
                  <p className="text-gray-600 mb-4">{post.excerpt}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.map(tag => (
                      <span key={tag} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                        #{tag}
                      </span>
                    ))}
                  </div>
                  <Link 
                    to={`/blog/${post.id}`} 
                    className="text-blue-600 font-medium hover:underline flex items-center"
                  >
                    Read more
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </motion.section>
        
        {/* Newsletter Subscribe section */}
        <section className="mt-16">
          <NewsletterSubscribeMain />
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-8 mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center mb-4 md:mb-0">
            <img src="/HunterFortress_Logo_Shield_Crosshairs_Optimized.png" alt="Logo" className="h-10 mr-3" />
            <span className="text-xl font-bold">HunterFortress</span>
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link to="/" className="hover:text-blue-300 transition font-semibold">Home</Link>
            <Link to="/contact" className="hover:text-blue-300 transition font-semibold">Contact</Link>
            <Link to="/storepal" className="hover:text-blue-300 transition font-semibold">StorePal</Link>
            <Link to="/terms" className="hover:text-blue-300 transition font-semibold">Terms</Link>
            <Link to="/privacy" className="hover:text-blue-300 transition">Privacy</Link>
            <Link to="/blog" className="hover:text-blue-300 transition">Blog</Link>
          </div>
        </div>
        <div className="text-center mt-6 pt-6 border-t border-blue-700">
          <p>&copy; 2025 HunterFortress. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;